/**
 * 動画書き出しの判断(#181 / [ADR-0036](../../../../docs/adr/0036-video-export-webcodecs.md) 決定 6)。
 *
 * **符号化そのものは検査できない**([ADR-0019](../../../../docs/adr/0019-dev-foundation.md))——
 * WebCodecs は jsdom で動かない。**長さ・寸法・本数の決め方だけをここに集めて単体テストする。**
 */

/** フレームレート。**30fps**(ADR-0036 決定 6)。 */
export const EXPORT_FPS = 30;

/**
 * 書き出しの長さの上限(秒)。**60 秒**(ADR-0036 決定 6)。
 *
 * **長い素材で待ち時間が青天井になるのを防ぐ。** 超える分は切り、切ったことを画面に出す。
 */
export const EXPORT_MAX_SECONDS = 60;

/**
 * 出力のビットレート(bps)。**1080p で 10Mbps**(ADR-0036 決定 6)。
 *
 * **書き出した動画は、このシステムに素材として戻ってきうる。**
 * 2026-08-12 に**複数動画が重かった原因は素材の約 50Mbps** と確定しており
 * ([HANDOVER.md](../../../../docs/HANDOVER.md) 3 節)、**自分が吐く動画で同じ問題を作らない。**
 */
const BITRATE_AT_1080P = 10_000_000;

/** 1080p の画素数。ビットレートを画素数に比例させる基準。 */
const PIXELS_AT_1080P = 1920 * 1080;

/**
 * **下限**(bps)。小さい盤面で極端に低いビットレートにすると、
 * **確認に使えない画質**になる。
 */
const BITRATE_FLOOR = 1_000_000;

export interface Size {
  readonly width: number;
  readonly height: number;
}

/**
 * 書き出しの長さ(秒)を決める。
 *
 * **最も長い動画素材 1 周**(ADR-0036 決定 6)—— 素材はループ再生(設計書 423)なので、
 * **1 周すれば全部の見え方が出る。**
 *
 * **上限 60 秒で切る。** 切ったかどうかは [`isTruncated`](#isTruncated) で分かる。
 *
 * **長さの取れない素材は数えない** —— `<video>` の `duration` は
 * メタデータが届くまで `NaN`、ストリームだと `Infinity` になる。
 * **混ざったまま最大値を取ると `NaN` が伝染する。**
 */
export function exportDuration(durations: readonly number[]): number {
  const usable = durations.filter((d) => Number.isFinite(d) && d > 0);
  if (usable.length === 0) return 0;
  return Math.min(Math.max(...usable), EXPORT_MAX_SECONDS);
}

/** 上限で切ったか(利用者に伝えるため)。 */
export function isTruncated(durations: readonly number[]): boolean {
  const usable = durations.filter((d) => Number.isFinite(d) && d > 0);
  if (usable.length === 0) return false;
  return Math.max(...usable) > EXPORT_MAX_SECONDS;
}

/**
 * 出力の寸法に見合うビットレート(bps)。
 *
 * **画素数に比例させる** —— 小さい絵に 10Mbps は無駄で、
 * **書き出しの時間とファイルの大きさだけが増える。**
 */
export function bitrateFor(size: Size): number {
  const pixels = size.width * size.height;
  if (!Number.isFinite(pixels) || pixels <= 0) return BITRATE_FLOOR;
  return Math.max(BITRATE_FLOOR, Math.round((pixels / PIXELS_AT_1080P) * BITRATE_AT_1080P));
}

/**
 * 進捗(0〜1)。
 *
 * **`total` が 0 でも壊れない** —— 0 除算で `NaN` が出ると、
 * 進捗の表示が「NaN%」になる。
 */
export function progressOf(done: number, total: number): number {
  if (!Number.isFinite(total) || total <= 0) return 0;
  const ratio = done / total;
  if (!Number.isFinite(ratio)) return 0;
  return Math.min(1, Math.max(0, ratio));
}
